/**
 * Who sells what. Each trader is keyed by NPC kind (see npcs.ts); Odile's market stall
 * opens her shop too. General traders buy anything at its listed value. Specialists only
 * buy their own trade's goods, but pay more for them.
 */
import { ITEMS, type ItemCat, type ItemId } from './items.js';
import { SHOP_BY_ID, type ShopEntry } from './crafting.js';
import { SKILL_NAMES, type SkillId } from './skills.js';
import { SUPPLIES, TOOLS, isSupplyId, isToolId, type SupplyId, type ToolId } from './tools.js';
import { GEAR, isGearId, type GearId } from './equipment.js';

export type PlainStockId = 'flour' | 'apples' | 'tinned_beans' | 'potato_seed' | 'onion_seed' | 'cabbage_seed' | 'wheat_seed' | 'plant_cure' | 'compost' | 'vial' | 'yarrow_seed' | 'comfrey_seed' | 'feverfew_seed' | 'sage_seed' | 'garlic_seed' | 'rosemary_seed' | 'redberry_seed' | 'mushroom_spawn' | 'flax_seed' | 'cowhide' | 'wool' | 'rabbit_pelt' | 'wolf_pelt' | 'boar_hide' | 'lurker_hide' | 'crawler_chitin' | 'wraith_silk';
export type StockId = ToolId | SupplyId | GearId | PlainStockId | ShopEntry['id'];
/** Plain items some traders keep, sold in bundles. Gear sells singly at its price, arrows in 25s. */
export const ITEM_STOCK: Partial<Record<string, { price: number; bundle: number; name: string; desc: string }>> = {
  flour: { price: 15, bundle: 5, name: 'Flour', desc: 'For bread and pies, baked in the oven.' },
  apples: { price: 30, bundle: 5, name: 'Apples', desc: 'From the old orchard. Two press into a jug of cider.' },
  tinned_beans: { price: 12, bundle: 1, name: 'Tin of beans', desc: 'Old-world food. Heals a little; never goes off.' },
  potato_seed: { price: 6, bundle: 3, name: 'Seed potatoes', desc: 'Three sow an allotment. Farming 1.' },
  onion_seed: { price: 9, bundle: 3, name: 'Onion sets', desc: 'Three sow an allotment. Farming 5.' },
  cabbage_seed: { price: 12, bundle: 3, name: 'Cabbage seeds', desc: 'Three sow an allotment. Farming 8.' },
  vial: { price: 20, bundle: 10, name: 'Empty bottles', desc: 'Ten glass bottles, washed. Fill them at a well for potions.' },
  yarrow_seed: { price: 20, bundle: 1, name: 'Yarrow seed', desc: 'For a herb patch. Farming 9.' },
  comfrey_seed: { price: 40, bundle: 1, name: 'Comfrey seed', desc: 'For a herb patch. Farming 16.' },
  feverfew_seed: { price: 75, bundle: 1, name: 'Feverfew seed', desc: 'For a herb patch. Farming 24.' },
  sage_seed: { price: 25, bundle: 1, name: 'Sage seed', desc: 'A kitchen herb, for a herb patch. Farming 14.' },
  garlic_seed: { price: 30, bundle: 3, name: 'Garlic cloves', desc: 'Three sow an allotment. Farming 20.' },
  redberry_seed: { price: 40, bundle: 1, name: 'Redberry seed', desc: 'For a bush patch. Grows once, fruits again and again. Farming 10.' },
  mushroom_spawn: { price: 30, bundle: 1, name: 'Mushroom spawn', desc: 'Push it into a mushroom log. Farming 18.' },
  flax_seed: { price: 55, bundle: 3, name: 'Flax seed', desc: 'Three sow an allotment. Farming 34. Spin the flax into bowstrings.' },
  rosemary_seed: { price: 60, bundle: 1, name: 'Rosemary seed', desc: 'A kitchen herb, for a herb patch. Farming 30.' },
  plant_cure: { price: 25, bundle: 1, name: 'Plant cure', desc: 'A bottle of something sharp. Treats one diseased crop.' },
  compost: { price: 20, bundle: 1, name: 'Compost', desc: 'Dig it into a patch before planting: less disease, a bigger crop.' },
  cowhide: { price: 75, bundle: 5, name: 'Cowhides', desc: 'Five raw hides. Tan them into leather at the rack.' },
  wool: { price: 30, bundle: 5, name: 'Wool', desc: 'Five fleeces. Spin them into yarn at the wheel: thread for sewing.' },
  rabbit_pelt: { price: 70, bundle: 5, name: 'Rabbit pelts', desc: 'Five pelts. Tanned, they make fur for furred leather. Crafting 10.' },
  wolf_pelt: { price: 45, bundle: 1, name: 'Wolf pelt', desc: 'Tanned, it makes wolf leather. Crafting 30.' },
  boar_hide: { price: 70, bundle: 1, name: 'Boar hide', desc: 'Tanned, it makes boar leather. Crafting 40.' },
  lurker_hide: { price: 180, bundle: 1, name: 'Lurker hide', desc: 'From the fen hunters, at a price. Tanned, it makes lurker leather. Crafting 52.' },
  crawler_chitin: { price: 260, bundle: 1, name: 'Crawler chitin', desc: 'Prised off rock crawlers by braver folk than Gilda. Crafting 66.' },
  wraith_silk: { price: 450, bundle: 1, name: 'Wraith silk', desc: 'Nobody will say where it comes from. Crafting 78.' },
  wheat_seed: { price: 18, bundle: 3, name: 'Seed wheat', desc: 'Three sow an allotment. Farming 12. Grind the wheat into flour at a quern.' },
};
/** Plain items a shop sells (not tools, supplies, gear or upgrades). */
export const isPlainStock = (id: string): id is PlainStockId => !!ITEM_STOCK[id];
export function stockDeal(id: StockId): { price: number; bundle: number } | null {
  if (isToolId(id)) return TOOLS[id].price ? { price: TOOLS[id].price!, bundle: 1 } : null;
  if (isSupplyId(id)) return { price: SUPPLIES[id].price, bundle: SUPPLIES[id].bundle };
  if (isGearId(id)) { const g = GEAR[id]; return g.price ? (g.slot === 'ammo' ? { price: g.price * 25, bundle: 25 } : { price: g.price, bundle: 1 }) : null; }
  return ITEM_STOCK[id] ?? null;
}

export interface ShopDef {
  /** Who runs it (shown in messages). */
  keeper: string;
  name: string;
  blurb: string;
  /** 'all' for a general trader. */
  buys: 'all' | ItemCat[];
  /** Multiplier on item value when this trader buys. */
  pays: number;
  stock: StockId[];
}

export const SHOPS: Record<string, ShopDef> = {
  odile: {
    keeper: 'Odile', name: "Odile's goods", blurb: 'Odile buys anything you carry and sells the basics.',
    buys: 'all', pays: 1, stock: ['pack', 'small_net', 'fishing_rod', 'hammer', 'bronze_hatchet', 'bronze_pick', 'bronze_sword', 'pine_shortbow', 'bronze_arrows', 'flour', 'bait', 'feathers'],
  },
  sal: {
    keeper: 'Sal', name: "Sal's trade crate", blurb: 'Sal is with the Drift. She buys fish and scrap, and sells what a castaway needs.',
    buys: 'all', pays: 1, stock: ['tinned_beans', 'bait', 'bronze_arrows'],
  },
  hal: {
    keeper: 'Hal', name: "Hal's axes and bows", blurb: 'Hal pays well for logs and pelts, and sells hatchets, bows and arrows.',
    buys: ['log', 'hide'], pays: 1.2, stock: ['bronze_hatchet', 'iron_hatchet', 'log_basket', 'pine_shortbow', 'oak_shortbow', 'oak_longbow', 'bronze_arrows', 'iron_arrows'],
  },
  corwin: {
    keeper: 'Corwin', name: "Corwin's picks", blurb: 'Corwin pays well for ore and gems, and sells pickaxes.',
    buys: ['ore', 'valuable'], pays: 1.2, stock: ['bronze_pick', 'iron_pick', 'leather_sack'],
  },
  maren: {
    keeper: 'Maren', name: "Maren's tackle", blurb: 'Every fish wants different gear. Maren has it, and buys raw fish.',
    buys: ['raw', 'trophy'], pays: 1.2, stock: ['small_net', 'fishing_rod', 'fly_rod', 'seine_net', 'eel_trap', 'crab_pot', 'iron_harpoon', 'lobster_cage', 'fish_barrel', 'bait', 'feathers'],
  },
  wenna: {
    keeper: 'Wenna', name: "Wenna's kitchen", blurb: 'Pans, pots, hooks, gauntlets and flour. Wenna buys food, meat and anything that goes in a pot.',
    buys: ['cooked', 'meat', 'ingredient'], pays: 1.2, stock: ['flour', 'apples', 'skillet', 'stewpot', 'smoking_hooks', 'gauntlets'],
  },
  bram: {
    keeper: 'Bram', name: "Bram's forge", blurb: 'Smithing gear, weapons and armour. Bram buys bars, fittings and anything forged.',
    buys: ['bar', 'fittings', 'gear'], pays: 1.2, stock: ['hammer', 'crucible', 'chisel', 'mould', 'glass_pipe', 'bronze_sword', 'bronze_battleaxe', 'bronze_helm', 'bronze_legs', 'bronze_body', 'iron_sword', 'iron_helm'],
  },
  marlow: {
    keeper: 'Marlow', name: "Marlow's seed shed", blurb: 'Seed, hoes and watering cans. Marlow buys anything you grow.',
    buys: ['ingredient', 'seed'], pays: 1.2, stock: ['potato_seed', 'onion_seed', 'cabbage_seed', 'wheat_seed', 'sage_seed', 'garlic_seed', 'rosemary_seed', 'compost', 'plant_cure', 'redberry_seed', 'mushroom_spawn', 'flax_seed', 'garden_hoe', 'iron_hoe', 'watering_can', 'large_can', 'brass_can', 'pruning_shears'],
  },
  hesper: {
    keeper: 'Hesper', name: "Hesper's remedies", blurb: 'Bottles and herb seed. Hesper buys herbs and potions.',
    buys: ['herb', 'potion'], pays: 1.2, stock: ['vial', 'yarrow_seed', 'comfrey_seed', 'feverfew_seed', 'plant_cure'],
  },
  gilda: {
    keeper: 'Gilda', name: "Gilda's hides", blurb: 'Hides, wool, needles and leather armour. Gilda buys hides and pelts.',
    buys: ['hide'], pays: 1.2, stock: ['needle', 'bone_needle', 'cowhide', 'wool', 'rabbit_pelt', 'wolf_pelt', 'boar_hide', 'lurker_hide', 'crawler_chitin', 'wraith_silk', 'leather_coif', 'leather_chaps', 'leather_body'],
  },
  ysolde: {
    keeper: 'Ysolde', name: "Ysolde's pack", blurb: 'Kit for the road, for those who have walked enough to need it.',
    buys: [], pays: 1, stock: ['charm', 'boots', 'lantern', 'steel_rod'],
  },
  dagny: {
    keeper: 'Dagny', name: "Dagny's ridge store", blurb: 'Dagny buys anything, and keeps iron gear for those who climb this far.',
    buys: 'all', pays: 1, stock: ['iron_hatchet', 'iron_pick', 'iron_battleaxe', 'iron_legs', 'iron_body', 'iron_arrows', 'crucible', 'bait', 'feathers'],
  },
};
export const isShopId = (v: unknown): v is string => typeof v === 'string' && Object.prototype.hasOwnProperty.call(SHOPS, v);

export const shopBuys = (s: ShopDef, id: ItemId) => !!ITEMS[id].value && (s.buys === 'all' || s.buys.includes(ITEMS[id].cat));
export const sellPrice = (s: ShopDef, id: ItemId) => Math.floor(ITEMS[id].value * s.pays);

const CAT_WORDS: Record<ItemCat, string> = { quest: 'quest items', log: 'logs', ore: 'ore', bar: 'bars', fittings: 'fittings', raw: 'raw fish', cooked: 'cooked fish', junk: 'junk', tool: 'tools', supply: 'supplies', trophy: 'trophies', treasure: 'curios', meat: 'raw meat', ingredient: 'ingredients', gear: 'weapons and armour', ammo: 'arrows', hide: 'hides and pelts', valuable: 'valuables', fletching: 'fletching parts', seed: 'seeds', herb: 'herbs', potion: 'potions' };
/** "logs", "bars and fittings", "anything". */
export function buysText(s: ShopDef) {
  if (s.buys === 'all') return 'anything of value';
  if (!s.buys.length) return 'nothing';
  const w = s.buys.map(c => CAT_WORDS[c]);
  return w.length === 1 ? w[0] : `${w.slice(0, -1).join(', ')} and ${w[w.length - 1]}`;
}

/** Display name, description and requirement for anything a shop can stock. */
export function stockInfo(id: StockId): { name: string; desc: string; req?: [SkillId, number]; kind: 'tool' | 'supply' | 'upgrade' | 'gear' | 'item' } {
  if (isToolId(id)) { const t = TOOLS[id]; return { name: t.name, desc: t.desc, req: t.use[1] > 1 ? t.use : undefined, kind: 'tool' }; }
  if (isSupplyId(id)) { const s = SUPPLIES[id]; return { name: `${s.name} ×${s.bundle}`, desc: s.desc, kind: 'supply' }; }
  if (isGearId(id)) { const g = GEAR[id]; return { name: g.slot === 'ammo' ? `${g.name} ×25` : g.name, desc: g.desc, req: g.req[1] > 1 ? g.req : undefined, kind: 'gear' }; }
  if (isPlainStock(id)) { const plain = ITEM_STOCK[id]!; return { name: plain.bundle > 1 ? `${plain.name} ×${plain.bundle}` : plain.name, desc: plain.desc, kind: 'item' }; }
  const e = SHOP_BY_ID[id];
  return { name: e.name, desc: e.desc, req: e.req, kind: 'upgrade' };
}

/** Names of the traders who stock `id`, e.g. "Maren or Odile". */
export function sellersOf(id: StockId): string {
  const names = Object.values(SHOPS).filter(s => s.stock.includes(id)).map(s => s.keeper);
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

export const reqText = (r: [SkillId, number]) => `${SKILL_NAMES[r[0]]} ${r[1]}`;
